// Reconciliation Redux Slice
// State management for the reconciliation workbench

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { reconciliationService } from '../services/reconciliationService';

import { 
    roundCurrency, 
    normalizeTinyNegativeBalanceAfter,
    isWorkersCompCaseType,
    isMVACaseType
} from '../utils/reconciliation.utils';

const hydrateAllocation = (allocation, allocationId = `${allocation.claimId}:${allocation.medicationId}`) => ({
    allocationId,
    isLocked: !!allocation.isLocked,
    ...allocation,
    isTouched: true, // Existing session allocations are always considered touched/active in the session
    balanceAfter: normalizeTinyNegativeBalanceAfter(allocation.balanceAfter),
});


const getAllocationClassification = (allocation = {}) => {
    if (allocation.isWriteOff) return 'WRITE_OFF';
    if (allocation.isDenied) return 'DENIED';
    if (allocation.isExhausted || allocation.adjustmentReasonCode === 'PIP_EXHAUSTION') return 'PIP_EXHAUSTED';
    return 'UNCLASSIFIED';
};

const appendClassificationHistory = (allocation, updates, toClassification) => {
    const fromClassification = getAllocationClassification(allocation);
    if (fromClassification === toClassification || fromClassification === 'UNCLASSIFIED') return updates;

    const existingHistory = Array.isArray(allocation.classificationHistory)
        ? allocation.classificationHistory
        : [];
    const reasonCode = updates.denialReasonCode || updates.adjustmentReasonCode || allocation.denialReasonCode || allocation.adjustmentReasonCode || undefined;
    const alreadyCaptured = existingHistory.some((entry) =>
        entry.from === fromClassification
        && entry.to === toClassification
        && entry.reasonCode === reasonCode
    );

    if (alreadyCaptured) return updates;

    return {
        ...updates,
        classificationHistory: [
            ...existingHistory,
            {
                from: fromClassification,
                to: toClassification,
                changedAt: new Date().toISOString(),
                reasonCode,
                notes: updates.adjustmentNotes || allocation.adjustmentNotes || undefined
            }
        ]
    };
};

// ============================================================================
// ASYNC THUNKS
// ============================================================================

export const getSession = createAsyncThunk(
    'reconciliation/getSession',
    async (sessionId, { rejectWithValue }) => {
        try {
            const response = await reconciliationService.getSession(sessionId);
            return response.session || response;
        } catch (error) {
            return rejectWithValue(error.message || 'Failed to fetch session');
        }
    }
);

export const syncFilters = createAsyncThunk(
    'reconciliation/syncFilters',
    async ({ sessionId, filters }, { rejectWithValue }) => {
        try {
            const response = await reconciliationService.updateFilters(sessionId, filters);
            return response;
        } catch (error) {
            return rejectWithValue(error.message || 'Failed to sync filters');
        }
    }
);

export const searchMedications = createAsyncThunk(
    'reconciliation/searchMedications',
    async (criteria, { rejectWithValue }) => {
        try {
            const response = await reconciliationService.searchMedications(criteria);
            return response;
        } catch (error) {
            return rejectWithValue(error.message || 'Failed to search medications');
        }
    }
);

export const createSession = createAsyncThunk(
    'reconciliation/createSession',
    async (sessionData, { rejectWithValue }) => {
        try {
            const response = await reconciliationService.createSession(sessionData);
            return response.session;
        } catch (error) {
            return rejectWithValue(error.message || 'Failed to create session');
        }
    }
);

export const updateSession = createAsyncThunk(
    'reconciliation/updateSession',
    async ({ sessionId, updates }, { rejectWithValue }) => {
        try {
            const response = await reconciliationService.updateSession(sessionId, updates);
            return response.session || response;
        } catch (error) {
            return rejectWithValue(error.message || 'Failed to update session');
        }
    }
);

export const appendAllocations = createAsyncThunk(
    'reconciliation/appendAllocations',
    async ({ sessionId, allocations }, { rejectWithValue }) => {
        try {
            const response = await reconciliationService.appendAllocations(sessionId, allocations);
            return response.session || response;
        } catch (error) {
            return rejectWithValue(error.message || 'Failed to append allocations');
        }
    }
);

export const calculateDistribution = createAsyncThunk(
    'reconciliation/calculateDistribution',
    async (payload, { rejectWithValue }) => {
        try {
            const response = await reconciliationService.calculateDistribution(payload);
            return response;
        } catch (error) {
            return rejectWithValue(error.message || 'Failed to calculate distribution');
        }
    }
);

export const applySession = createAsyncThunk(
    'reconciliation/applySession',
    async ({ sessionId, allocations, interestAmount, interestAllocations }, { rejectWithValue }) => {
        try {
            const response = await reconciliationService.applySession(sessionId, allocations, {
                interestAmount,
                interestAllocations
            });
            return response;
        } catch (error) {
            return rejectWithValue(error.message || 'Failed to apply session');
        }
    }
);

export const listSessions = createAsyncThunk(
    'reconciliation/listSessions',
    async (filters, { rejectWithValue }) => {
        try {
            const response = await reconciliationService.listSessions(filters);
            return response;
        } catch (error) {
            return rejectWithValue(error.message || 'Failed to list sessions');
        }
    }
);

export const voidSession = createAsyncThunk(
    'reconciliation/voidSession',
    async ({ sessionId, reason }, { rejectWithValue }) => {
        try {
            const response = await reconciliationService.voidSession(sessionId, reason);
            return response.session;
        } catch (error) {
            return rejectWithValue(error.message || 'Failed to void session');
        }
    }
);

export const reopenSession = createAsyncThunk(
    'reconciliation/reopenSession',
    async ({ sessionId, reason }, { rejectWithValue }) => {
        try {
            const response = await reconciliationService.reopenSession(sessionId, reason);
            return response.session;
        } catch (error) {
            return rejectWithValue(error.message || 'Failed to reopen session');
        }
    }
);

export const deleteSession = createAsyncThunk(
    'reconciliation/deleteSession',
    async (sessionId, { rejectWithValue }) => {
        try {
            const response = await reconciliationService.deleteSession(sessionId);
            return { sessionId, success: response.success };
        } catch (error) {
            return rejectWithValue(error.message || 'Failed to delete session');
        }
    }
);

export const removeAllocations = createAsyncThunk(
    'reconciliation/removeAllocations',
    async ({ sessionId, targetAllocations, reason }, { rejectWithValue }) => {
        try {
            const response = await reconciliationService.removeAllocations(sessionId, targetAllocations, reason);
            return response.session;
        } catch (error) {
            return rejectWithValue(error.message || 'Failed to remove allocations');
        }
    }
);

export const fetchSuspenseByPayer = createAsyncThunk(
    'reconciliation/fetchSuspenseByPayer',
    async (payerName, { rejectWithValue }) => {
        try {
            const response = await reconciliationService.getSuspenseByPayer(payerName);
            return response.sessions || response;
        } catch (error) {
            return rejectWithValue(error.message || 'Failed to fetch suspense items');
        }
    }
);

// ============================================================================
// INITIAL STATE
// ============================================================================

const initialState = {
    // Current Session
    currentSession: null,

    // Search Results
    searchResults: [],
    searchMetadata: {
        totalRxCount: 0,
        baseTotalItems: 0,
        requiresPrimaryFilter: false
    },
    searchNextCursor: null,
    searchLoading: false,
    searchError: null,

    // Selected Medications for posting
    selectedMedications: [],

    // Allocations (work grid state)
    allocations: {},  // keyed by medicationId

    // Payment Metadata
    paymentMetadata: {
        paymentType: 'STANDARD',
        paymentMethod: 'CHECK',
        checkTotal: 0,
        checkNumber: '',
        checkDate: '',
        payerName: '',
        distributionStrategy: 'PROPORTIONAL',
        isSettlement: false,
        settlementPercentage: null,
        grossSettlement: null,
        attorneyFees: null,
        // Document references
        eobDocumentId: null,
        checkDocumentId: null,
        settlementLetterDocumentId: null,
        // Distribution targets
        copayTotal: 0,
        deductibleTotal: 0,
    },

    // Interest
    interestAmount: 0,
    interestAllocations: [],

    // UI State
    isFinderCollapsed: false,
    isSelectedTrayCollapsed: false,

    // Calculated Totals
    totals: {
        checkTotal: 0,
        allocated: 0,
        interestTotal: 0,
        remaining: 0,
        copayTotal: 0,
        deductibleTotal: 0,
        writeOffTotal: 0,
    },

    // History
    sessions: [],
    sessionsLoading: false,
    sessionsNextCursor: null,
    sessionsTotalCheckAmount: 0,

    // Grid Filters (Persisted in session)
    currentFilters: {
        patientName: '',
        rxNumber: '',
        insuranceName: '',
        insuranceClaimId: '',
        globalSearch: '',
        dosStart: '',
        dosEnd: '',
    },

    // Status
    loading: false,
    error: null,
    success: null,

    // Suspense / Unallocated Funds
    suspenseItems: [],
    suspenseLoading: false,
};

// Case type helpers migrated to shared utilities


// ============================================================================
// SLICE
// ============================================================================

const reconciliationSlice = createSlice({
    name: 'reconciliation',
    initialState,
    reducers: {
        // Reset state
        resetReconciliation: () => initialState,

        // Clear errors/success
        clearMessages: (state) => {
            state.error = null;
            state.success = null;
        },

        // Update payment metadata
        setPaymentMetadata: (state, action) => {
            state.paymentMetadata = { ...state.paymentMetadata, ...action.payload };
            state.totals.checkTotal = action.payload.checkTotal ?? state.paymentMetadata.checkTotal;
            reconciliationSlice.caseReducers.recalculateTotals(state);
        },

        // Toggle panels
        toggleFinderCollapsed: (state) => {
            state.isFinderCollapsed = !state.isFinderCollapsed;
        },

        toggleSelectedTrayCollapsed: (state) => {
            state.isSelectedTrayCollapsed = !state.isSelectedTrayCollapsed;
        },

        // Select/Deselect medications
        selectMedication: (state, action) => {
            const med = action.payload;
            const key = `${med.claimId}:${med.medicationId}`;
            const exists = state.selectedMedications.find(m => `${m.claimId}:${m.medicationId}` === key);
            if (exists) return;
            state.selectedMedications.push(med);
            const derivedPatientName = (med.patientName || `${med.firstName || ''} ${med.lastName || ''}`.trim() || 'Unknown').trim();
            // Initialize allocation for this medication
            const totalOutstanding = (med.billedAmount || 0) - (med.paidAmount || 0) - (med.writeOffAmount || 0);
            state.allocations[key] = {
                allocationId: key, // Unique ID for UI and lookup
                claimId: med.claimId,
                patientId: med.patientId,
                patientName: derivedPatientName,
                medicationId: med.medicationId,
                caseType: med.caseType || '',
                status: med.status || med.medicationStatus || '',
                medicationStatus: med.medicationStatus || med.status || '',
                claimStatus: med.claimStatus || '',
                rxNumber: med.rxNumber,
                medicationName: med.medicationName,
                dateOfService: med.dateOfService,
                insuranceClaimId: med.insuranceClaimId || '',
                dateOfInjury: med.dateOfInjury || null,
                diagnosisCodes: med.diagnosisCodes || [],
                jurisdictionState: med.jurisdictionState || '',
                previousPaymentDate: med.previousPaymentDate || null,
                billedAmount: med.billedAmount || 0,
                previousPaid: med.paidAmount || 0,
                previousWriteOff: med.writeOffAmount || 0,
                currentPayment: 0,
                interestAmount: 0,
                copayAmount: 0,
                deductibleAmount: 0,
                writeOffAmount: 0,
                isTouched: false,
                balanceAfter: normalizeTinyNegativeBalanceAfter(totalOutstanding),
                insuranceName: med.insuranceName || 'Unknown',
                eobHistory: med.eobHistory || [],
                adjustmentNotes: '',
                adjustmentEntries: [],
                isDenied: !!med.isDenied,
                isExhausted: !!med.isExhausted,
                isWriteOff: !!med.isWriteOff,
                isLocked: false,
                denialReasonCode: '',
                markAsReconciled: !!med.markAsReconciled,
                isInAttorneyBucket: !!med.isInAttorneyBucket,
                allocationTags: []
            };
        },

        selectMultipleMedications: (state, action) => {
            const medications = action.payload;
            if (!Array.isArray(medications)) return;

            medications.forEach(med => {
                const key = `${med.claimId}:${med.medicationId}`;
                const exists = state.selectedMedications.find(m => `${m.claimId}:${m.medicationId}` === key);
                if (!exists) {
                    state.selectedMedications.push(med);
                    const derivedPatientName = (med.patientName || `${med.firstName || ''} ${med.lastName || ''}`.trim() || 'Unknown').trim();
                    // Initialize allocation for this medication
                    const totalOutstanding = (med.billedAmount || 0) - (med.paidAmount || 0) - (med.writeOffAmount || 0);
                    state.allocations[key] = {
                        allocationId: key,
                        claimId: med.claimId,
                        patientId: med.patientId,
                        patientName: derivedPatientName,
                        medicationId: med.medicationId,
                        caseType: med.caseType || '',
                        status: med.status || med.medicationStatus || '',
                        medicationStatus: med.medicationStatus || med.status || '',
                        claimStatus: med.claimStatus || '',
                        rxNumber: med.rxNumber,
                        medicationName: med.medicationName,
                        dateOfService: med.dateOfService,
                        insuranceClaimId: med.insuranceClaimId || '',
                        dateOfInjury: med.dateOfInjury || null,
                        diagnosisCodes: med.diagnosisCodes || [],
                        jurisdictionState: med.jurisdictionState || '',
                        previousPaymentDate: med.previousPaymentDate || null,
                        billedAmount: med.billedAmount || 0,
                        previousPaid: med.paidAmount || 0,
                        previousWriteOff: med.writeOffAmount || 0,
                        currentPayment: 0,
                        interestAmount: 0,
                        copayAmount: 0,
                        deductibleAmount: 0,
                        writeOffAmount: 0,
                        isTouched: false,
                        balanceAfter: normalizeTinyNegativeBalanceAfter(totalOutstanding),
                        insuranceName: med.insuranceName || 'Unknown',
                        eobHistory: med.eobHistory || [],
                        adjustmentNotes: '',
                        adjustmentEntries: [],
                        isDenied: !!med.isDenied,
                        isExhausted: !!med.isExhausted,
                        isWriteOff: !!med.isWriteOff,
                        isLocked: false,
                        denialReasonCode: '',
                        markAsReconciled: !!med.markAsReconciled,
                        isInAttorneyBucket: !!med.isInAttorneyBucket,
                        allocationTags: []
                    };
                }
            });
            reconciliationSlice.caseReducers.recalculateTotals(state);
        },

        deselectMedication: (state, action) => {
            const allocationKey = action.payload; // Now expects "claimId:medicationId"
            state.selectedMedications = state.selectedMedications.filter(m => `${m.claimId}:${m.medicationId}` !== allocationKey);
            delete state.allocations[allocationKey];
            reconciliationSlice.caseReducers.recalculateTotals(state);
        },

        deselectMedications: (state, action) => {
            const keys = Array.isArray(action.payload) ? action.payload : [];
            if (keys.length === 0) return;
            const keySet = new Set(keys);
            state.selectedMedications = state.selectedMedications.filter(
                m => !keySet.has(`${m.claimId}:${m.medicationId}`)
            );
            for (const key of keySet) {
                delete state.allocations[key];
            }
            reconciliationSlice.caseReducers.recalculateTotals(state);
        },

        clearSelectedMedications: (state) => {
            state.selectedMedications = [];
            state.allocations = {};
            reconciliationSlice.caseReducers.recalculateTotals(state);
        },

        // Update allocation for a medication
        updateAllocation: (state, action) => {
            // Support both legacy `medicationId` and current `allocationId` payload keys.
            const allocationKey = action.payload?.allocationId ?? action.payload?.medicationId;
            const rawUpdates = action.payload?.updates || {};
            if (state.allocations[allocationKey]) {
                const alloc = state.allocations[allocationKey];
                const isWorkersComp = isWorkersCompCaseType(alloc.caseType);
                let updates = isWorkersComp
                    ? {
                        ...rawUpdates,
                        ...(rawUpdates.copayAmount !== undefined ? { copayAmount: 0 } : {}),
                        ...(rawUpdates.deductibleAmount !== undefined ? { deductibleAmount: 0 } : {}),
                    }
                    : rawUpdates;
                if (updates.isDenied === true) {
                    updates.isExhausted = false;
                    updates.isWriteOff = false;
                }
                if (updates.isExhausted === true) {
                    updates.isDenied = false;
                    updates.isWriteOff = false;
                }
                if (updates.isWriteOff === true) {
                    updates.isDenied = false;
                    updates.isExhausted = false;
                    updates.denialReasonCode = '';
                    if (updates.adjustmentReasonCode === 'PIP_EXHAUSTION') {
                        updates.adjustmentReasonCode = '';
                    }
                }
                updates = appendClassificationHistory(
                    alloc,
                    updates,
                    getAllocationClassification({ ...alloc, ...updates })
                );
                const wcGuards = isWorkersComp ? { copayAmount: 0, deductibleAmount: 0 } : {};

                // Unified calculation logic for all numeric fields
                const currentPayment = updates.currentPayment !== undefined ? parseFloat(updates.currentPayment) || 0 : parseFloat(alloc.currentPayment) || 0;
                const copayAmount = isWorkersComp ? 0 : (updates.copayAmount !== undefined ? parseFloat(updates.copayAmount) || 0 : parseFloat(alloc.copayAmount) || 0);
                const deductibleAmount = isWorkersComp ? 0 : (updates.deductibleAmount !== undefined ? parseFloat(updates.deductibleAmount) || 0 : parseFloat(alloc.deductibleAmount) || 0);
                const writeOffAmount = updates.writeOffAmount !== undefined ? parseFloat(updates.writeOffAmount) || 0 : alloc.writeOffAmount;

                const totalOutstanding = alloc.billedAmount - alloc.previousPaid - (alloc.previousWriteOff || 0);
                const totalDeductions = currentPayment + copayAmount + deductibleAmount;

                // Check if any "touching" field is updated
                const isTouched = updates.currentPayment !== undefined ||
                    updates.interestAmount !== undefined ||
                    updates.copayAmount !== undefined ||
                    updates.deductibleAmount !== undefined ||
                    updates.writeOffAmount !== undefined ||
                    updates.denialReasonCode !== undefined ||
                    updates.adjustmentReasonCode !== undefined ||
                    updates.adjustmentNotes !== undefined ||
                    updates.isDenied !== undefined ||
                    updates.isExhausted !== undefined ||
                    updates.isWriteOff !== undefined ||
                    updates.isInAttorneyBucket !== undefined ||
                    updates.isLocked !== undefined ||
                    updates.allocationTags !== undefined ||
                    updates.adjustmentEntries !== undefined ||
                    updates.markAsReconciled !== undefined;

                const resultingIsReconciled = updates.markAsReconciled !== undefined ? updates.markAsReconciled : alloc.markAsReconciled;
                const shouldAutoZeroBalance = resultingIsReconciled;

                if (updates.writeOffAmount !== undefined) {
                    // If user EXPLICITLY updates write-off, recalculate balanceAfter
                    const balanceAfter = totalOutstanding - totalDeductions - writeOffAmount;
                    state.allocations[allocationKey] = {
                        ...alloc,
                        ...updates,
                        ...wcGuards,
                        isTouched: alloc.isTouched || isTouched,
                        balanceAfter: Math.max(0, roundCurrency(balanceAfter))
                    };
                } else if (updates.currentPayment !== undefined || updates.copayAmount !== undefined || updates.deductibleAmount !== undefined) {
                    // Core Requirement: Write-off absorbs changes to stay at 0 balance if marked as reconciled
                    if (shouldAutoZeroBalance) {
                        const newWriteOff = totalOutstanding - totalDeductions;
                        state.allocations[allocationKey] = {
                            ...alloc,
                            ...updates,
                            ...wcGuards,
                            isTouched: true,
                            writeOffAmount: Math.round(Math.max(0, newWriteOff) * 100) / 100,
                            balanceAfter: 0
                        };
                    } else {
                        // If NOT reconciled, let the balance absorb the change
                        const balanceAfter = totalOutstanding - totalDeductions - alloc.writeOffAmount;
                        state.allocations[allocationKey] = {
                            ...alloc,
                            ...updates,
                            ...wcGuards,
                            isTouched: true,
                            balanceAfter: Math.max(0, normalizeTinyNegativeBalanceAfter(balanceAfter))
                        };
                    }
                } else {
                    // Other fields (notes, reason, toggles)
                    state.allocations[allocationKey] = { ...alloc, ...updates, ...wcGuards };

                    if (isTouched) {
                        state.allocations[allocationKey].isTouched = true;
                    }

                    // markAsReconciled toggle: ensure balance is 0 and write-off is Correct
                    if (shouldAutoZeroBalance) {
                        const newWriteOff = totalOutstanding - totalDeductions;
                        state.allocations[allocationKey].writeOffAmount = Math.round(Math.max(0, newWriteOff) * 100) / 100;
                        state.allocations[allocationKey].balanceAfter = 0;
                    } else if (updates.markAsReconciled === false) {
                        // When unmarking, recalculate balance after applying existing write-off
                        const balanceAfter = totalOutstanding - totalDeductions - alloc.writeOffAmount;
                        state.allocations[allocationKey].balanceAfter = Math.max(0, normalizeTinyNegativeBalanceAfter(balanceAfter));
                    }
                }
            }

            // ========================================================================
            // AUTO-LOGIC: PIP Exhausted -> Attorney Bucket (MVA Only)
            // If the user marked this row as PIP exhausted AND it is an MVA case, 
            // we automatically mark all other rows with the same Rx number as Attorney Bucket.
            // ========================================================================
            if (rawUpdates.isExhausted === true) {
                const targetAlloc = state.allocations[allocationKey];
                if (targetAlloc && targetAlloc.rxNumber && isMVACaseType(targetAlloc.caseType)) {
                    const targetRx = targetAlloc.rxNumber;
                    Object.values(state.allocations).forEach(a => {
                        if (a.rxNumber === targetRx && isMVACaseType(a.caseType) && !a.isInAttorneyBucket) {
                            a.isInAttorneyBucket = true;
                            a.isTouched = true;
                        }
                    });
                }
            }


            reconciliationSlice.caseReducers.recalculateTotals(state);
        },

        // Apply calculated distribution to allocations
        applyDistribution: (state, action) => {
            const { allocations: calculatedAllocations } = action.payload;
            const arg = action.meta?.arg || {};
            const { includePayment, includeCopay, includeDeductible } = arg;
            const mergeWithExisting = !!arg.mergeWithExisting;

            for (const calc of calculatedAllocations) {
                const key = `${calc.claimId}:${calc.medicationId}`;
                const alloc = state.allocations[key];
                if (alloc) {
                    const isWorkersComp = isWorkersCompCaseType(alloc.caseType);

                    if (includePayment !== false) {
                        alloc.currentPayment = mergeWithExisting
                            ? (parseFloat(alloc.currentPayment) || 0) + (parseFloat(calc.suggestedPayment) || 0)
                            : (parseFloat(calc.suggestedPayment) || 0);
                    }

                    if (!isWorkersComp && includeCopay !== false) {
                        alloc.copayAmount = mergeWithExisting
                            ? (parseFloat(alloc.copayAmount) || 0) + (parseFloat(calc.suggestedCopay) || 0)
                            : (parseFloat(calc.suggestedCopay) || 0);
                    } else if (isWorkersComp) {
                        alloc.copayAmount = 0;
                    }

                    if (!isWorkersComp && includeDeductible !== false) {
                        alloc.deductibleAmount = mergeWithExisting
                            ? (parseFloat(alloc.deductibleAmount) || 0) + (parseFloat(calc.suggestedDeductible) || 0)
                            : (parseFloat(calc.suggestedDeductible) || 0);
                    } else if (isWorkersComp) {
                        alloc.deductibleAmount = 0;
                    }

                    const totalOutstanding = alloc.billedAmount - alloc.previousPaid - (alloc.previousWriteOff || 0);
                    const balanceAfter = totalOutstanding - alloc.currentPayment - alloc.copayAmount - alloc.deductibleAmount - (alloc.writeOffAmount || 0);

                    state.allocations[key].balanceAfter = Math.max(0, normalizeTinyNegativeBalanceAfter(balanceAfter));
                    
                    // Auto-distribution only "touches" the row if it actually suggested a value change
                    const hasSuggestion = (includePayment !== false && Math.abs(parseFloat(calc.suggestedPayment) || 0) > 0.001) ||
                                         (!isWorkersComp && includeCopay !== false && Math.abs(parseFloat(calc.suggestedCopay) || 0) > 0.001) ||
                                         (!isWorkersComp && includeDeductible !== false && Math.abs(parseFloat(calc.suggestedDeductible) || 0) > 0.001);
                                         
                    if (hasSuggestion) {
                        alloc.isTouched = true;
                    }
                }
            }
            reconciliationSlice.caseReducers.recalculateTotals(state);
        },

        // Toggle all medications as reconciled
        toggleAllReconciled: (state, action) => {
            const checked = action.payload;
            const allocationIds = Object.keys(state.allocations);

            allocationIds.forEach(id => {
                const alloc = state.allocations[id];
                const totalOutstanding = alloc.billedAmount - alloc.previousPaid - (alloc.previousWriteOff || 0);
                const isWorkersComp = isWorkersCompCaseType(alloc.caseType);
                const copayAmount = isWorkersComp ? 0 : (alloc.copayAmount || 0);
                const deductibleAmount = isWorkersComp ? 0 : (alloc.deductibleAmount || 0);
                const wcGuards = isWorkersComp ? { copayAmount: 0, deductibleAmount: 0 } : {};

                if (checked) {
                    if (alloc.isDenied && !(alloc.denialReasonCode || '').trim()) {
                        const deniedBalance = totalOutstanding - alloc.currentPayment - copayAmount - deductibleAmount;
                        state.allocations[id] = {
                            ...alloc,
                            ...wcGuards,
                            markAsReconciled: false,
                            isTouched: true,
                            writeOffAmount: 0,
                            balanceAfter: Math.max(0, normalizeTinyNegativeBalanceAfter(deniedBalance))
                        };
                    } else {
                        // Mark as reconciled: 0 balance, everything else to write-off
                        const newWriteOff = totalOutstanding - alloc.currentPayment - copayAmount - deductibleAmount;
                        state.allocations[id] = {
                            ...alloc,
                            ...wcGuards,
                            markAsReconciled: true,
                            isTouched: true,
                            writeOffAmount: Math.round(Math.max(0, newWriteOff) * 100) / 100,
                            balanceAfter: 0
                        };
                    }
                } else {
                    // Unmark: balance absorbs the difference
                    const balanceAfter = totalOutstanding - alloc.currentPayment - copayAmount - deductibleAmount - alloc.writeOffAmount;
                    state.allocations[id] = {
                        ...alloc,
                        ...wcGuards,
                        markAsReconciled: false,
                        balanceAfter: normalizeTinyNegativeBalanceAfter(balanceAfter)
                    };
                }
            });

            reconciliationSlice.caseReducers.recalculateTotals(state);
        },

        // Set interest amount
        setInterestAmount: (state, action) => {
            state.interestAmount = action.payload;
        },

        // Recalculate totals
        recalculateTotals: (state) => {
            const allocs = Object.values(state.allocations);
            const allocated = allocs.reduce((sum, a) => sum + (a.currentPayment || 0), 0);
            const copayTotal = allocs.reduce((sum, a) => sum + (a.copayAmount || 0), 0);
            const deductibleTotal = allocs.reduce((sum, a) => sum + (a.deductibleAmount || 0), 0);
            const interestTotal = allocs.reduce((sum, a) => sum + (a.interestAmount || 0), 0);
            // Dynamic Write-off: Only sum items that have been "touched" by the user
            const writeOffTotal = allocs.reduce((sum, a) => sum + (a.isTouched ? (a.writeOffAmount || 0) : 0), 0);

            let remaining = 0;
            if (state.paymentMetadata.paymentType === 'SETTLEMENT') {
                remaining = state.paymentMetadata.checkTotal - allocated - copayTotal - deductibleTotal;
            } else if (state.paymentMetadata.paymentType === 'INTEREST') {
                // For interest payments, we might want to track remaining against a check total,
                // but usually interest is just allocated directly. We'll subtract from checkTotal.
                remaining = state.paymentMetadata.checkTotal - interestTotal;
            } else {
                remaining = state.paymentMetadata.checkTotal - allocated;
            }

            state.totals = {
                checkTotal: state.paymentMetadata.checkTotal,
                allocated: Math.round(allocated * 100) / 100,
                interestTotal: Math.round(interestTotal * 100) / 100,
                remaining: Math.round(remaining * 100) / 100,
                copayTotal: Math.round(copayTotal * 100) / 100,
                remainingCopay: Math.round(((parseFloat(state.paymentMetadata.copayTotal) || 0) - copayTotal) * 100) / 100,
                deductibleTotal: Math.round(deductibleTotal * 100) / 100,
                remainingDeductible: Math.round(((parseFloat(state.paymentMetadata.deductibleTotal) || 0) - deductibleTotal) * 100) / 100,
                writeOffTotal: Math.round(writeOffTotal * 100) / 100,
            };
        },

        // Set persistent grid filters
        setGridFilters: (state, action) => {
            state.currentFilters = { ...state.currentFilters, ...action.payload };
        },

        // Add a recoupment row to the work grid
        addRecoupmentRow: (state, action) => {
            const { linkedSession } = action.payload || {};
            const key = `recoupment_${Date.now()}`;

            state.allocations[key] = {
                allocationId: key,
                claimId: 'PAYER_LEVEL',
                patientId: 'PAYER_LEVEL',
                patientName: 'Payer Recoupment',
                medicationId: key,
                caseType: 'SPECIAL',
                rxNumber: linkedSession?.checkNumber || 'Manual',
                medicationName: `Recoupment offset - ${linkedSession?.checkNumber || 'Payer'}`,
                dateOfService: new Date().toISOString().split('T')[0],
                insuranceClaimId: 'N/A',
                dateOfInjury: null,
                diagnosisCodes: [],
                jurisdictionState: '',
                previousPaymentDate: null,
                billedAmount: 0,
                previousPaid: 0,
                previousWriteOff: 0,
                currentPayment: 0, // User should enter a negative value
                interestAmount: 0,
                copayAmount: 0,
                deductibleAmount: 0,
                writeOffAmount: 0,
                isTouched: true,
                markAsReconciled: true,
                balanceAfter: 0,
                isRecoupment: true,
                isWriteOff: false,
                recoupmentLinkedSessionId: linkedSession?.id || null,
                adjustmentNotes: linkedSession?.id ? `Recoupment linked to session ${linkedSession.id}` : 'Manual recoupment'
            };

            // Recalculate totals
            reconciliationSlice.caseReducers.recalculateTotals(state);
        },

        // Add a suspense row to the work grid for extra amount
        addSuspenseRow: (state) => {
            const key = `suspense_${Date.now()}`;

            state.allocations[key] = {
                allocationId: key,
                claimId: 'PAYER_LEVEL',
                patientId: 'PAYER_LEVEL',
                patientName: 'Excess Funds / Suspense',
                medicationId: key,
                caseType: 'SPECIAL',
                rxNumber: 'Session Extra',
                medicationName: 'Unallocated Session Funds',
                dateOfService: new Date().toISOString().split('T')[0],
                insuranceClaimId: 'N/A',
                dateOfInjury: null,
                diagnosisCodes: [],
                jurisdictionState: '',
                previousPaymentDate: null,
                billedAmount: 0,
                previousPaid: 0,
                previousWriteOff: 0,
                currentPayment: 0,
                interestAmount: 0,
                copayAmount: 0,
                deductibleAmount: 0,
                writeOffAmount: 0,
                isTouched: true,
                markAsReconciled: true,
                balanceAfter: 0,
                isSuspense: true,
                isWriteOff: false,
                adjustmentNotes: 'Extra amount tied to session, for future reconciliation/recoupment.'
            };

            // Recalculate totals
            reconciliationSlice.caseReducers.recalculateTotals(state);
        },

        clearSuspense: (state) => {
            state.suspenseItems = [];
            state.suspenseError = null;
        },

        // Import allocations directly (e.g. from Agentic EOB)
        importAgenticAllocations: (state, action) => {
            const allocationsList = action.payload; // Array of IReconciliationAllocation
            if (!Array.isArray(allocationsList)) return;

            allocationsList.forEach(alloc => {
                const key = alloc.allocationId || `${alloc.claimId}:${alloc.medicationId}`;
                
                // Add to selected medications if not already there
                const exists = state.selectedMedications.find(m => `${m.claimId}:${m.medicationId}` === `${alloc.claimId}:${alloc.medicationId}`);
                if (!exists) {
                    state.selectedMedications.push({
                        claimId: alloc.claimId,
                        medicationId: alloc.medicationId,
                        patientId: alloc.patientId,
                        patientName: alloc.patientName,
                        rxNumber: alloc.rxNumber,
                        medicationName: alloc.medicationName,
                        dateOfService: alloc.dateOfService,
                        billedAmount: alloc.billedAmount,
                        paidAmount: alloc.previousPaid,
                        writeOffAmount: alloc.previousWriteOff,
                        insuranceName: alloc.insuranceName,
                        caseType: alloc.caseType,
                        insuranceClaimId: alloc.insuranceClaimId
                    });
                }

                // Update/Set allocation
                state.allocations[key] = {
                    ...alloc,
                    allocationId: key,
                    isTouched: true // Always touched if coming from AI
                };
            });

            reconciliationSlice.caseReducers.recalculateTotals(state);
        }
    },
    extraReducers: (builder) => {
        // Search Medications
        builder
            .addCase(searchMedications.pending, (state) => {
                state.searchLoading = true;
                state.searchError = null;
            })
            .addCase(searchMedications.fulfilled, (state, action) => {
                state.searchLoading = false;
                const payload = action.payload || {};
                const data = payload.data || payload;
                const medications = data.medications || (Array.isArray(data) ? data : []);
                const shouldAppend = !!action.meta.arg?.cursor;
                if (shouldAppend) {
                    const existing = new Map((state.searchResults || []).map((med) => [`${med.claimId}:${med.medicationId}`, med]));
                    medications.forEach((med) => existing.set(`${med.claimId}:${med.medicationId}`, med));
                    state.searchResults = Array.from(existing.values());
                } else {
                    state.searchResults = medications;
                }
                state.searchNextCursor = data.nextCursor || payload.nextCursor || null;
                
                const baseMetadata = shouldAppend
                    ? state.searchMetadata
                    : {
                        totalRxCount: 0,
                        baseTotalItems: 0,
                        requiresPrimaryFilter: false,
                        hasMore: false,
                        pagedDosSearch: false,
                        scannedClaims: 0,
                        nextCursor: null
                    };
                const incomingMetadata = data.metadata || payload.metadata || {};
                state.searchMetadata = { ...baseMetadata, ...incomingMetadata };
                if (state.searchMetadata.pagedDosSearch) {
                    const previousScanned = shouldAppend ? Number(baseMetadata.scannedClaims || 0) : 0;
                    const currentScanned = Number(incomingMetadata.scannedClaims || 0);
                    state.searchMetadata.scannedClaims = previousScanned + currentScanned;
                    state.searchMetadata.loadedMedicationCount = state.searchResults.length;
                    state.searchMetadata.totalRxCount = state.searchResults.length;
                }
                state.searchMetadata.nextCursor = state.searchNextCursor;

                // If this search was triggered as part of a session restore
                if (action.meta.arg?.isRestore) {
                    medications.forEach(med => {
                        const key = `${med.claimId}:${med.medicationId}`;
                        const existingAlloc = state.allocations[key];
                        const derivedPatientName = (med.patientName || `${med.firstName || ''} ${med.lastName || ''}`.trim() || 'Unknown').trim();
                        
                        // If it doesn't exist, we add it from search results
                        if (!existingAlloc) {
                            // SPLIT PAYMENT HANDLING:
                            // med.paidAmount = Posted (Completed)
                            // med.pendingPaidAmount = Draft/In-progress (All sessions)
                            // For a NEW allocation, all pending amount is "previous" to this one.
                            const previousPaid = (med.paidAmount || 0) + (med.pendingPaidAmount || 0);
                            const previousWriteOff = (med.writeOffAmount || 0) + (med.pendingWriteOffAmount || 0);
                            
                            const totalOutstanding = (med.billedAmount || 0) - previousPaid - previousWriteOff;
                            
                            state.selectedMedications.push(med);
                            state.allocations[key] = {
                                allocationId: key,
                                claimId: med.claimId,
                                patientId: med.patientId,
                                patientName: derivedPatientName,
                                medicationId: med.medicationId,
                                caseType: med.caseType || '',
                                status: med.status || med.medicationStatus || '',
                                medicationStatus: med.medicationStatus || med.status || '',
                                claimStatus: med.claimStatus || '',
                                rxNumber: med.rxNumber,
                                medicationName: med.medicationName,
                                dateOfService: med.dateOfService,
                                insuranceClaimId: med.insuranceClaimId || '',
                                dateOfInjury: med.dateOfInjury || null,
                                diagnosisCodes: med.diagnosisCodes || [],
                                jurisdictionState: med.jurisdictionState || '',
                                previousPaymentDate: med.previousPaymentDate || null,
                                billedAmount: med.billedAmount || 0,
                                previousPaid,
                                previousWriteOff,
                                currentPayment: 0,
                                interestAmount: 0,
                                copayAmount: 0,
                                deductibleAmount: 0,
                                writeOffAmount: 0,
                                isTouched: false,
                                markAsReconciled: false,
                                balanceAfter: normalizeTinyNegativeBalanceAfter(totalOutstanding),
                                insuranceName: med.insuranceName || 'Unknown',
                                eobHistory: med.eobHistory || [],
                                adjustmentNotes: '',
                                adjustmentEntries: [],
                                isDenied: !!med.isDenied,
                                isExhausted: !!med.isExhausted,
                                isLocked: false,
                                denialReasonCode: med.denialReasonCode || '',
                                allocationTags: med.allocationTags || []
                            };
                        } else {
                            // If it exists (it was a saved delta), we update its "base" info from search results
                            // but keep the user-modified fields.
                            // DEDUCTIVE LOGIC: Subtract this session's currentPayment from the total pending amount.
                            const currentSessionPayment = state.allocations[key].currentPayment || 0;
                            const currentSessionWriteOff = state.allocations[key].writeOffAmount || 0;
                            
                            const previousPaid = (med.paidAmount || 0) + Math.max(0, (med.pendingPaidAmount || 0) - currentSessionPayment);
                            const previousWriteOff = (med.writeOffAmount || 0) + Math.max(0, (med.pendingWriteOffAmount || 0) - currentSessionWriteOff);

                            state.allocations[key] = {
                                ...state.allocations[key],
                                dateOfService: med.dateOfService || state.allocations[key].dateOfService,
                                billedAmount: med.billedAmount || 0,
                                previousPaid,
                                previousWriteOff,
                                insuranceName: med.insuranceName || state.allocations[key].insuranceName,
                                dateOfInjury: med.dateOfInjury || state.allocations[key].dateOfInjury,
                                diagnosisCodes: med.diagnosisCodes || state.allocations[key].diagnosisCodes,
                                jurisdictionState: med.jurisdictionState || state.allocations[key].jurisdictionState,
                                // Recalculate balanceAfter based on new base values if not markAsReconciled
                                ...(!state.allocations[key].markAsReconciled ? {
                                    balanceAfter: Math.max(0, normalizeTinyNegativeBalanceAfter(med.billedAmount - previousPaid - previousWriteOff - state.allocations[key].currentPayment - state.allocations[key].copayAmount - state.allocations[key].deductibleAmount - state.allocations[key].writeOffAmount))
                                } : {})
                            };

                            // Update in selectedMedications too
                            const selIdx = state.selectedMedications.findIndex(m => `${m.claimId}:${m.medicationId}` === key);
                            if (selIdx !== -1) {
                                state.selectedMedications[selIdx] = { ...state.selectedMedications[selIdx], ...med };
                            }
                        }
                    });
                    reconciliationSlice.caseReducers.recalculateTotals(state);
                }
            })
            .addCase(searchMedications.rejected, (state, action) => {
                state.searchLoading = false;
                state.searchError = action.payload;
                state.searchNextCursor = null;
                state.searchMetadata = {
                    totalRxCount: 0,
                    baseTotalItems: 0,
                    requiresPrimaryFilter: false,
                    nextCursor: null
                };
            });

        // Create Session
        builder
            .addCase(createSession.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(createSession.fulfilled, (state, action) => {
                state.loading = false;
                state.currentSession = action.payload;
            })
            .addCase(createSession.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });

        // Update Session (Draft Save)
        builder
            .addCase(updateSession.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updateSession.fulfilled, (state, action) => {
                state.loading = false;
                state.currentSession = action.payload;
                state.success = 'Draft saved';
            })
            .addCase(updateSession.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });

        // Append Allocations
        builder
            .addCase(appendAllocations.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(appendAllocations.fulfilled, (state, action) => {
                state.loading = false;
                state.currentSession = action.payload;
                state.success = 'Allocations appended';
            })
            .addCase(appendAllocations.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });

        // Get Session
        builder
            .addCase(getSession.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getSession.fulfilled, (state, action) => {
                state.loading = false;
                state.currentSession = action.payload;
                state.paymentMetadata = {
                    ...state.paymentMetadata,
                    paymentType: action.payload.paymentType || state.paymentMetadata.paymentType,
                    paymentMethod: action.payload.paymentMethod || state.paymentMetadata.paymentMethod,
                    checkTotal: action.payload.checkTotal || 0,
                    checkNumber: action.payload.checkNumber || '',
                    checkDate: action.payload.checkDate || '',
                    payerName: action.payload.payerName || '',
                    distributionStrategy: action.payload.distributionStrategy || state.paymentMetadata.distributionStrategy,
                    isSettlement: !!action.payload.isSettlement,
                    settlementPercentage: action.payload.settlementPercentage ?? null,
                    grossSettlement: action.payload.grossSettlement ?? null,
                    attorneyFees: action.payload.attorneyFees ?? null,
                    eobDocumentId: action.payload.eobDocumentId ?? null,
                    checkDocumentId: action.payload.checkDocumentId ?? null,
                    settlementLetterDocumentId: action.payload.settlementLetterDocumentId ?? null,
                    copayTotal: action.payload.copayTotal || 0,
                    deductibleTotal: action.payload.deductibleTotal || 0,
                };
                if (action.payload.currentFilters) {
                    state.currentFilters = action.payload.currentFilters;
                }
                // If allocations are present in session, load them
                if (action.payload.allocations) {
                    state.allocations = {};
                    state.selectedMedications = action.payload.allocations.map(a => ({
                        ...a,
                        medicationId: a.medicationId
                    }));
                    action.payload.allocations.forEach(a => {
                        const key = `${a.claimId}:${a.medicationId}`;
                        state.allocations[key] = hydrateAllocation(a, key);
                    });
                    reconciliationSlice.caseReducers.recalculateTotals(state);
                }
            })
            .addCase(getSession.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });

        // Calculate Distribution
        builder
            .addCase(calculateDistribution.fulfilled, (state, action) => {
                reconciliationSlice.caseReducers.applyDistribution(state, action);
            });

        // Apply Session
        builder
            .addCase(applySession.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(applySession.fulfilled, (state, action) => {
                state.loading = false;
                const payload = action.payload || {};
                const data = payload.data || payload;
                state.success = `Successfully posted ${data.medicationsUpdated || 0} medications`;
                state.currentSession = data.session || data;
                // Keep completed rows visible in the active work grid after Apply.
                // If backend returns updated allocations, hydrate local state from that payload.
                if (Array.isArray(data?.session?.allocations)) {
                    const nextAllocations = {};
                    data.session.allocations.forEach((alloc) => {
                        if (!alloc?.medicationId || !alloc?.claimId) return;
                        const key = `${alloc.claimId}:${alloc.medicationId}`;
                        nextAllocations[key] = hydrateAllocation(alloc, key);
                    });
                    if (Object.keys(nextAllocations).length > 0) {
                        state.allocations = nextAllocations;
                        state.selectedMedications = data.session.allocations.map((a) => ({
                            ...a,
                            medicationId: a.medicationId
                        }));
                    }
                }
                reconciliationSlice.caseReducers.recalculateTotals(state);
            })
            .addCase(applySession.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });

        // List Sessions
        builder
            .addCase(listSessions.pending, (state) => {
                state.sessionsLoading = true;
            })
            .addCase(listSessions.fulfilled, (state, action) => {
                state.sessionsLoading = false;
                const payload = action.payload || {};
                const data = payload.data || payload;
                state.sessions = data.sessions || (Array.isArray(data) ? data : []);
                state.sessionsNextCursor = data.nextCursor || payload.nextCursor;
                state.sessionsTotalCheckAmount = data.totalCheckAmount || 0;
            })
            .addCase(listSessions.rejected, (state) => {
                state.sessionsLoading = false;
            });

        // Void Session
        builder
            .addCase(voidSession.fulfilled, (state, action) => {
                state.success = 'Session voided successfully';
                // Update the session in the list
                const idx = state.sessions.findIndex(s => s.id === action.payload.id);
                if (idx >= 0) {
                    state.sessions[idx] = action.payload;
                }
            })
            .addCase(reopenSession.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(reopenSession.fulfilled, (state, action) => {
                state.loading = false;
                state.currentSession = action.payload;
                state.success = 'Session reopened for editing';
                // Update in list if present
                const idx = state.sessions.findIndex(s => s.id === action.payload.id);
                if (idx >= 0) {
                    state.sessions[idx] = action.payload;
                }
            })
            .addCase(reopenSession.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(fetchSuspenseByPayer.pending, (state) => {
                state.suspenseLoading = true;
            })
            .addCase(fetchSuspenseByPayer.fulfilled, (state, action) => {
                state.suspenseLoading = false;
                state.suspenseItems = action.payload;
            })
            .addCase(fetchSuspenseByPayer.rejected, (state) => {
                state.suspenseLoading = false;
            })
            .addCase(deleteSession.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(deleteSession.fulfilled, (state, action) => {
                state.loading = false;
                state.success = 'Session deleted successfully';
                state.currentSession = null;
                state.sessions = state.sessions.filter(s => s.id !== action.payload.sessionId);
            })
            .addCase(deleteSession.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // Remove Allocations
            .addCase(removeAllocations.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(removeAllocations.fulfilled, (state, action) => {
                state.loading = false;
                state.currentSession = action.payload;
                // Update local allocations and selected medications
                const nextAllocations = {};
                const nextSelected = [];
                if (action.payload.allocations) {
                    action.payload.allocations.forEach(a => {
                        const key = `${a.claimId}:${a.medicationId}`;
                        nextAllocations[key] = hydrateAllocation(a, key);
                        nextSelected.push({
                            ...a,
                            medicationId: a.medicationId
                        });
                    });
                }
                state.allocations = nextAllocations;
                state.selectedMedications = nextSelected;
                state.success = 'Allocations removed successfully';
                reconciliationSlice.caseReducers.recalculateTotals(state);
            })
            .addCase(removeAllocations.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });
    }
});

export const {
    resetReconciliation,
    clearMessages,
    setPaymentMetadata,
    toggleFinderCollapsed,
    toggleSelectedTrayCollapsed,
    selectMedication,
    selectMultipleMedications,
    deselectMedication,
    deselectMedications,
    clearSelectedMedications,
    updateAllocation,
    applyDistribution,
    toggleAllReconciled,
    setInterestAmount,
    setGridFilters,
    addRecoupmentRow,
    addSuspenseRow,
    clearSuspense,
    importAgenticAllocations
} = reconciliationSlice.actions;

export default reconciliationSlice.reducer;
